/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.0.0
 */
import type { TOpenLabelEditorAction } from '@components/Table/types/tableTypes';
import { createAction } from '@modules/app/createAction';

import {
    LABEL_EDITOR_CREATE,
    LABEL_EDITOR_CREATE_FAILED,
    LABEL_EDITOR_CREATE_SUCCESSFUL,
    LABEL_EDITOR_LOAD_LIST,
    LABEL_EDITOR_LOAD_LIST_FAILED,
    LABEL_EDITOR_LOAD_LIST_SUCCESSFUL,
    LABEL_EDITOR_SEARCH,
    LABEL_EDITOR_SWITCH,
    LABEL_EDITOR_UPDATE,
    LABEL_EDITOR_UPDATE_FAILED,
    LABEL_EDITOR_UPDATE_SUCCESSFUL,
} from './labelEditorConstants';
import {
    IEditableLabel,
    TLabelEditorCreateAction,
    TLabelEditorCreateFailedAction,
    TLabelEditorCreateSuccessfulAction,
    TLabelEditorLoadListAction,
    TLabelEditorLoadListFailedAction,
    TLabelEditorLoadListSuccessfulAction,
    TLabelEditorSearchAction,
    TLabelEditorSwitchAction,
    TLabelEditorUpdateAction,
    TLabelEditorUpdateFailedAction,
    TLabelEditorUpdateSuccessfulAction,
} from './labelEditorTypes';


/*
* -------------------------------------------
*          LOAD LABEL LIST
* -------------------------------------------
*/

export interface ILabelEditorLoadListPayload {
    clearList?: boolean;
}

export const labelEditorLoadList = (payload: ILabelEditorLoadListPayload): TLabelEditorLoadListAction =>
    createAction(LABEL_EDITOR_LOAD_LIST, payload);

export const labelEditorLoadListSuccessful = (payload: IEditableLabel[]): TLabelEditorLoadListSuccessfulAction =>
    createAction(LABEL_EDITOR_LOAD_LIST_SUCCESSFUL, payload);

export const labelEditorLoadListFailed = (payload: string): TLabelEditorLoadListFailedAction =>
    createAction(LABEL_EDITOR_LOAD_LIST_FAILED, payload);


/*
* -------------------------------------------
*          CREATE LABEL
* -------------------------------------------
*/

export const labelEditorCreate = (payload: string): TLabelEditorCreateAction =>
    createAction(LABEL_EDITOR_CREATE, payload);

export const labelEditorCreateSuccessful = (payload: IEditableLabel): TLabelEditorCreateSuccessfulAction =>
    createAction(LABEL_EDITOR_CREATE_SUCCESSFUL, payload);

export const labelEditorCreateFailed = (payload: string): TLabelEditorCreateFailedAction =>
    createAction(LABEL_EDITOR_CREATE_FAILED, payload);


/*
* -------------------------------------------
*          UPDATE LABEL LIST
* -------------------------------------------
*/

export const labelEditorUpdate = (payload: IEditableLabel[]): TLabelEditorUpdateAction =>
    createAction(LABEL_EDITOR_UPDATE, payload);

export const labelEditorUpdateSuccessful = (): TLabelEditorUpdateSuccessfulAction =>
    createAction(LABEL_EDITOR_UPDATE_SUCCESSFUL);

export const labelEditorUpdateFailed = (payload: string): TLabelEditorUpdateFailedAction =>
    createAction(LABEL_EDITOR_UPDATE_FAILED, payload);


/*
* -------------------------------------------
*          SEARCH LABELS
* -------------------------------------------
*/

export const labelEditorSearch = (payload: string): TLabelEditorSearchAction =>
    createAction(LABEL_EDITOR_SEARCH, payload);

/*
* -------------------------------------------
*          SWITCH LABELS
* -------------------------------------------
*/

export const labelEditorSwitch = (payload: IEditableLabel[]): TLabelEditorSwitchAction =>
    createAction(LABEL_EDITOR_SWITCH, payload);


export type LabelEditorActionsType =
    TLabelEditorCreateAction
    | TLabelEditorCreateFailedAction
    | TLabelEditorCreateSuccessfulAction
    | TLabelEditorLoadListAction
    | TLabelEditorLoadListFailedAction
    | TLabelEditorLoadListSuccessfulAction
    | TLabelEditorSearchAction
    | TLabelEditorSwitchAction
    | TLabelEditorUpdateAction
    | TLabelEditorUpdateFailedAction
    | TLabelEditorUpdateSuccessfulAction
    | TOpenLabelEditorAction
    ;
